import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Smartphone, Wifi, WifiOff, Trash2, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  online: 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20',
  offline: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/20',
  limited: 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20',
  error: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/20',
};

export default function GatewaysPage() {
  const [gateways, setGateways] = useState([]);
  const [overview, setOverview] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'gsm_modem' });
  const [saving, setSaving] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const [g, o] = await Promise.all([api.get('/gateways'), api.get('/gateways/overview')]);
      setGateways(g.data.items);
      setOverview(o.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const r = await api.post('/gateways', form);
      setNewApiKey(r.data.api_key);
      toast.success('Bramka dodana');
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac bramke?')) return;
    try { await api.delete(`/gateways/${id}`); toast.success('Usunieto'); fetch(); }
    catch { toast.error('Blad'); }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('Klucz skopiowany');
  };

  return (
    <div className="space-y-6" data-testid="gateways-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Bramki SMS</h1>
          <p className="text-sm text-muted-foreground mt-1">Zarzadzaj telefonami i modemami GSM</p>
        </div>
        <Button onClick={() => { setNewApiKey(null); setDialogOpen(true); }} data-testid="gateways-add-button"><Plus className="h-4 w-4 mr-2" />Dodaj bramke</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-4 pb-3 px-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[hsl(var(--success))]/10 flex items-center justify-center"><Wifi className="h-4 w-4 text-[hsl(var(--success))]" /></div>
              <div><p className="text-xs text-muted-foreground">Online</p><p className="text-xl font-semibold font-heading tabular-nums" data-testid="gateways-online-count">{overview.online || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-4 pb-3 px-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[hsl(var(--destructive))]/10 flex items-center justify-center"><WifiOff className="h-4 w-4 text-[hsl(var(--destructive))]" /></div>
              <div><p className="text-xs text-muted-foreground">Offline</p><p className="text-xl font-semibold font-heading tabular-nums" data-testid="gateways-offline-count">{overview.offline || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-4 pb-3 px-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center"><Smartphone className="h-4 w-4 text-primary" /></div>
              <div><p className="text-xs text-muted-foreground">Lacznie</p><p className="text-xl font-semibold font-heading tabular-nums">{overview.total || 0}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/50 bg-card/80">
        <CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div> : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Nazwa</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ostatni heartbeat</TableHead>
                  <TableHead>Wyslane dzis</TableHead>
                  <TableHead>Limit/dzien</TableHead>
                  <TableHead className="w-20">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gateways.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Brak bramek. Dodaj pierwsza!</TableCell></TableRow>
                ) : gateways.map(g => (
                  <TableRow key={g.id} className="hover:bg-muted/30 transition-colors duration-150">
                    <TableCell>
                    <div>
                      <span className="font-medium">{g.name}</span>
                      <p className="text-[10px] font-mono text-muted-foreground cursor-pointer hover:text-primary" onClick={() => {navigator.clipboard.writeText(g.id); toast.success('Gateway ID skopiowany')}} title="Kliknij aby skopiowac ID">ID: {g.id.slice(0,8)}... <Copy className="h-2.5 w-2.5 inline" /></p>
                      {g.api_key && <p className="text-[10px] font-mono text-muted-foreground cursor-pointer hover:text-primary" onClick={() => {navigator.clipboard.writeText(g.api_key); toast.success('API Key skopiowany')}} title="Kliknij aby skopiowac API Key">Key: {g.api_key.slice(0,10)}... <Copy className="h-2.5 w-2.5 inline" /></p>}
                    </div></TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{g.type === 'gsm_modem' ? 'Modem GSM' : 'Telefon'}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[g.status] || ''}>{g.status}</Badge></TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{g.last_heartbeat ? new Date(g.last_heartbeat).toLocaleString('pl') : 'Nigdy'}</TableCell>
                    <TableCell className="font-mono text-sm tabular-nums">{g.daily_stats?.sent || 0}</TableCell>
                    <TableCell className="font-mono text-sm tabular-nums">{g.device_info?.sim_count || g.modems_detail?.length || 0} SIM</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(g.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>


      {/* SIM Cards Detail */}
      {gateways.filter(g => g.modems_detail && g.modems_detail.length > 0).map(g => (
        <Card key={`sims-${g.id}`} className="border-border/50 bg-card/80 mt-4">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Karty SIM: {g.name} ({g.modems_detail.length} modemow)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {g.modems_detail.map((m, i) => (
                <div key={i} className="p-2.5 rounded-lg border border-border/30 bg-muted/20 hover:bg-muted/40 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-mono font-medium">{m.port}</span>
                    <Badge variant="outline" className={`text-[9px] h-4 ${m.sim_ready ? 'text-[hsl(var(--success))] border-[hsl(var(--success))]/30' : 'text-destructive'}`}>
                      {m.sim_ready ? 'SIM OK' : 'BRAK'}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium font-mono">{m.phone || '?'}</p>
                  <p className="text-[10px] text-muted-foreground">{m.operator || '?'} | Sig: {m.signal || 0}/31</p>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                    <span className="text-[hsl(var(--success))]">{m.sent || 0} wys</span>
                    <span className="text-destructive">{m.failed || 0} bl</span>
                    <span className="ml-auto">{m.limits?.max_per_day || 800}/d</span>
                  </div>
                  <p className="text-[9px] font-mono text-muted-foreground/50 mt-0.5">{m.imei || ''}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Dodaj bramke SMS</DialogTitle><DialogDescription>Skonfiguruj nowe urzadzenie</DialogDescription></DialogHeader>
          {newApiKey ? (
            <div className="space-y-4">
              <p className="text-sm text-[hsl(var(--success))]">Bramka utworzona! Zapisz klucz API:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-3 rounded-lg bg-muted text-xs font-mono break-all">{newApiKey}</code>
                <Button variant="outline" size="icon" onClick={() => copyKey(newApiKey)} data-testid="gateway-copy-key"><Copy className="h-4 w-4" /></Button>
              </div>
              <p className="text-xs text-muted-foreground">Ten klucz wyswietli sie tylko raz. Uzyj go w konfiguracji agenta.</p>
              <Button className="w-full" onClick={() => setDialogOpen(false)}>Zamknij</Button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Nazwa bramki</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="np. Modem-Biuro-01" data-testid="gateway-form-name" /></div>
                <div className="space-y-2">
                  <Label>Typ</Label>
                  <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                    <SelectTrigger data-testid="gateway-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gsm_modem">Modem GSM</SelectItem>
                      <SelectItem value="android_phone">Telefon Android</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setDialogOpen(false)}>Anuluj</Button>
                <Button onClick={handleCreate} disabled={saving || !form.name} data-testid="gateway-form-submit">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Dodaj
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
